import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Switch,
  Alert,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Inventory as InventoryIcon,
  AttachMoney as MoneyIcon,
  Category as CategoryIcon,
} from '@mui/icons-material';
import { useFormContext, useFieldArray } from 'react-hook-form';

import { StoreFormData, ProductData } from './types/store.types';

const ProductsForm: React.FC = () => {
  const { control, register, formState: { errors } } = useFormContext<StoreFormData>();
  const { fields, append, remove, update } = useFieldArray({
    control,
    name: 'products',
  });

  const [editingProduct, setEditingProduct] = useState<number | null>(null);
  const [newProduct, setNewProduct] = useState<Partial<ProductData>>({
    name: '',
    description: '',
    price: 0,
    originalPrice: 0,
    quantity: 1,
    category: 'general',
    tags: [],
    images: [],
    isActive: true,
    featured: false,
  });

  // Categorias de produtos
  const productCategories = [
    'general',
    'electronics',
    'clothing',
    'home',
    'beauty',
    'sports',
    'books',
    'toys',
    'automotive',
    'food',
    'art',
    'religious',
    'other',
  ];

  const handleAddProduct = () => {
    if (editingProduct !== null) {
      // Editando produto existente
      update(editingProduct, newProduct as ProductData);
      setEditingProduct(null);
    } else {
      // Adicionando novo produto
      append(newProduct as ProductData);
    }
    setNewProduct({
      name: '',
      description: '',
      price: 0,
      originalPrice: 0,
      quantity: 1,
      category: 'general',
      tags: [],
      images: [],
      isActive: true,
      featured: false,
    });
  };

  const handleEditProduct = (index: number) => {
    const product = fields[index] as ProductData;
    setNewProduct(product);
    setEditingProduct(index);
  };

  const handleDeleteProduct = (index: number) => {
    remove(index);
  };

  const handleCancelEdit = () => {
    setEditingProduct(null);
    setNewProduct({
      name: '',
      description: '',
      price: 0,
      originalPrice: 0,
      quantity: 1,
      category: 'general',
      tags: [],
      images: [],
      isActive: true,
      featured: false,
    });
  };

  const addTag = (tag: string) => {
    if (tag && !newProduct.tags?.includes(tag)) {
      setNewProduct({
        ...newProduct,
        tags: [...(newProduct.tags || []), tag],
      });
    }
  };

  const removeTag = (tagToRemove: string) => {
    setNewProduct({
      ...newProduct,
      tags: newProduct.tags?.filter(tag => tag !== tagToRemove),
    });
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
        <InventoryIcon color="primary" />
        Produtos da Loja
      </Typography>

      {/* Lista de produtos existentes */}
      {fields.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Produtos Adicionados ({fields.length})
          </Typography>
          <Grid container spacing={2}>
            {fields.map((field, index) => {
              const product = field as ProductData;
              return (
                <Grid item xs={12} md={6} lg={4} key={field.id}>
                  <Card>
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                        <Typography variant="h6" noWrap sx={{ flexGrow: 1, mr: 1 }}>
                          {product.name}
                        </Typography>
                        <Box>
                          <IconButton
                            size="small"
                            onClick={() => handleEditProduct(index)}
                            color="primary"
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteProduct(index)}
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                      </Box>
                      
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {product.description}
                      </Typography>
                      
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <MoneyIcon color="success" fontSize="small" />
                        <Typography variant="h6" color="success.main">
                          R$ {product.price.toFixed(2)}
                        </Typography>
                        {product.originalPrice && product.originalPrice > product.price && (
                          <Typography variant="body2" color="text.secondary" sx={{ textDecoration: 'line-through' }}>
                            R$ {product.originalPrice.toFixed(2)}
                          </Typography>
                        )}
                      </Box>
                      
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <InventoryIcon fontSize="small" />
                        <Typography variant="body2">
                          Estoque: {product.quantity}
                        </Typography>
                      </Box>
                      
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <CategoryIcon fontSize="small" />
                        <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                          {product.category}
                        </Typography>
                      </Box>
                      
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {product.tags?.map((tag, tagIndex) => (
                          <Chip
                            key={tagIndex}
                            label={tag}
                            size="small"
                            variant="outlined"
                          />
                        ))}
                      </Box>
                      
                      <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                        {product.featured && (
                          <Chip label="Destaque" size="small" color="primary" />
                        )}
                        {!product.isActive && (
                          <Chip label="Inativo" size="small" color="error" />
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      )}

      {/* Botão para adicionar/editar produto */}
      <Button
        variant="outlined"
        startIcon={<AddIcon />}
        onClick={() => setEditingProduct(null)}
        sx={{ mb: 3 }}
      >
        {editingProduct !== null ? 'Adicionar Novo Produto' : 'Adicionar Produto'}
      </Button>

      {/* Formulário de produto */}
      {(editingProduct !== null || fields.length === 0) && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              {editingProduct !== null ? 'Editar Produto' : 'Novo Produto'}
            </Typography>
            
            <Grid container spacing={2}>
              {/* Nome do produto */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Nome do Produto"
                  value={newProduct.name || ''}
                  onChange={(e) => setNewProduct({ ...newProduct, name: e.target.value })}
                />
              </Grid>

              {/* Categoria */}
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Categoria</InputLabel>
                  <Select
                    value={newProduct.category || 'general'}
                    label="Categoria"
                    onChange={(e) => setNewProduct({ ...newProduct, category: e.target.value })}
                  >
                    {productCategories.map((category) => (
                      <MenuItem key={category} value={category} sx={{ textTransform: 'capitalize' }}>
                        {category}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Descrição */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Descrição do Produto"
                  value={newProduct.description || ''}
                  onChange={(e) => setNewProduct({ ...newProduct, description: e.target.value })}
                />
              </Grid>

              {/* Preço */}
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Preço de Venda"
                  type="number"
                  value={newProduct.price || 0}
                  onChange={(e) => setNewProduct({ ...newProduct, price: parseFloat(e.target.value) || 0 })}
                  InputProps={{
                    startAdornment: <Typography sx={{ mr: 1 }}>R$</Typography>,
                  }}
                />
              </Grid>

              {/* Preço original */}
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Preço Original (opcional)"
                  type="number"
                  value={newProduct.originalPrice || 0}
                  onChange={(e) => setNewProduct({ ...newProduct, originalPrice: parseFloat(e.target.value) || 0 })}
                  InputProps={{
                    startAdornment: <Typography sx={{ mr: 1 }}>R$</Typography>,
                  }}
                />
              </Grid>

              {/* Quantidade */}
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Quantidade em Estoque"
                  type="number"
                  value={newProduct.quantity || 1}
                  onChange={(e) => setNewProduct({ ...newProduct, quantity: parseInt(e.target.value) || 1 })}
                />
              </Grid>

              {/* Tags */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Tags (pressione Enter para adicionar)"
                  placeholder="Ex: promocao, novo, destaque"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      const target = e.target as HTMLInputElement;
                      addTag(target.value.trim());
                      target.value = '';
                    }
                  }}
                />
                <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {newProduct.tags?.map((tag, index) => (
                    <Chip
                      key={index}
                      label={tag}
                      size="small"
                      onDelete={() => removeTag(tag)}
                    />
                  ))}
                </Box>
              </Grid>

              {/* Opções */}
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" gutterBottom>
                  Opções do Produto
                </Typography>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={newProduct.featured || false}
                        onChange={(e) => setNewProduct({ ...newProduct, featured: e.target.checked })}
                      />
                    }
                    label="Produto em Destaque"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={newProduct.isActive || false}
                        onChange={(e) => setNewProduct({ ...newProduct, isActive: e.target.checked })}
                      />
                    }
                    label="Produto Ativo"
                  />
                </Box>
              </Grid>
            </Grid>

            <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button onClick={handleCancelEdit} variant="outlined">
                Cancelar
              </Button>
              <Button
                onClick={handleAddProduct}
                variant="contained"
                disabled={!newProduct.name || !newProduct.description || (newProduct.price || 0) <= 0}
              >
                {editingProduct !== null ? 'Salvar Alterações' : 'Adicionar Produto'}
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Validação de erros */}
      {errors.products && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {errors.products.message}
        </Alert>
      )}

      {/* Dicas */}
      <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
        <Typography variant="subtitle2" gutterBottom color="primary">
          💡 Dicas para seus produtos:
        </Typography>
        <Typography variant="body2" color="text.secondary">
          • Use nomes claros e descritivos<br />
          • Adicione descrições detalhadas dos produtos<br />
          • Configure preços competitivos<br />
          • Mantenha o estoque atualizado<br />
          • Use tags para facilitar a busca<br />
          • Destaque seus produtos mais importantes
        </Typography>
      </Box>
    </Box>
  );
};

export default ProductsForm;
